import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProjectShowcase = ({ projects, onVote, onComment }) => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  
  const categories = ['All', 'AI/ML', 'Robotics', 'Web Dev', 'Mobile', 'IoT'];

  const filteredProjects = selectedCategory === 'All' 
    ? projects 
    : projects.filter(project => project.category === selectedCategory);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Community Projects</h2>
          <p className="text-muted-foreground">Discover amazing creations from our community</p>
        </div>
        <Button
          variant="default"
          iconName="Plus"
          iconPosition="left"
          className="morphing-cta"
        >
          Share Project
        </Button>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
              selectedCategory === category
                ? 'bg-accent text-white shadow-glow'
                : 'bg-muted/30 text-muted-foreground hover:bg-muted/50 hover:text-foreground'
            }`}
          >
            {category}
          </button>
        ))}
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProjects.map((project) => (
          <div key={project.id} className="holographic-card group">
            {/* Project Image */}
            <div className="relative overflow-hidden rounded-t-lg h-48">
              <Image
                src={project.image}
                alt={project.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent"></div>
              
              {/* Category Badge */}
              <div className="absolute top-3 left-3">
                <span className="px-2 py-1 bg-accent/90 text-white text-xs font-medium rounded-full">
                  {project.category}
                </span>
              </div>

              {/* Featured Badge */}
              {project.featured && (
                <div className="absolute top-3 right-3">
                  <div className="w-8 h-8 bg-warning rounded-full flex items-center justify-center">
                    <Icon name="Star" size={16} color="white" fill="currentColor" />
                  </div>
                </div>
              )}
            </div>

            <div className="p-6">
              {/* Project Header */}
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-accent transition-colors duration-300">
                  {project.title}
                </h3>
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {project.description}
                </p>
              </div>

              {/* Creator Info */}
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 rounded-full overflow-hidden">
                  <Image
                    src={project.creator.avatar}
                    alt={project.creator.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{project.creator.name}</p>
                  <p className="text-xs text-muted-foreground">{project.timeAgo}</p>
                </div>
              </div>

              {/* Technologies */}
              <div className="flex flex-wrap gap-2 mb-4">
                {project.technologies.map((tech, index) => (
                  <span 
                    key={index}
                    className="px-2 py-1 bg-muted/30 text-xs text-muted-foreground rounded-md"
                  >
                    {tech}
                  </span>
                ))}
              </div>

              {/* Project Stats */}
              <div className="flex items-center justify-between mb-4 py-3 border-t border-border/50">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => onVote(project.id)}
                    className="flex items-center space-x-1 text-muted-foreground hover:text-accent transition-colors duration-300"
                  >
                    <Icon name="Heart" size={16} />
                    <span className="text-sm">{project.likes}</span>
                  </button>
                  <button
                    onClick={() => onComment(project.id)}
                    className="flex items-center space-x-1 text-muted-foreground hover:text-accent transition-colors duration-300"
                  >
                    <Icon name="MessageCircle" size={16} />
                    <span className="text-sm">{project.comments}</span>
                  </button>
                </div>
                <div className="flex items-center space-x-1 text-muted-foreground">
                  <Icon name="Eye" size={16} />
                  <span className="text-sm">{project.views}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ExternalLink"
                  iconPosition="left"
                  className="flex-1"
                >
                  View Demo
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Github"
                  iconPosition="left"
                  className="flex-1"
                >
                  Source
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Load More */}
      <div className="text-center">
        <Button
          variant="outline"
          iconName="ChevronDown"
          iconPosition="right"
        >
          Load More Projects
        </Button>
      </div>
    </div>
  );
};

export default ProjectShowcase;